import {
  CognitoUserPool,
  CognitoUser,
  CognitoUserAttribute,
  AuthenticationDetails,
} from 'amazon-cognito-identity-js';

const UserPoolId = process.env.REACT_APP_COGNITO_USER_POOL_ID;
const ClientId = process.env.REACT_APP_COGNITO_CLIENT_ID;

export const userPool =
  UserPoolId && ClientId ? new CognitoUserPool({ UserPoolId, ClientId }) : null;

export const PORTAL_ROUTE_BY_GROUP = {
  parent: '/portal/parent',
  consultant: '/portal/consultant',
  admin: '/portal/admin',
};

function readGroup(idTokenPayload) {
  const groups = idTokenPayload['cognito:groups'] || [];
  return ['admin', 'consultant', 'parent'].find((g) => groups.includes(g)) || null;
}

export function signIn(email, password) {
  return new Promise((resolve, reject) => {
    if (!userPool) {
      reject(new Error('Cognito is not configured. Set REACT_APP_COGNITO_* env vars.'));
      return;
    }
    const user = new CognitoUser({ Username: email, Pool: userPool });
    const details = new AuthenticationDetails({ Username: email, Password: password });
    user.authenticateUser(details, {
      onSuccess: (session) => {
        const payload = session.getIdToken().decodePayload();
        resolve({
          email: payload.email,
          name:
            [payload.given_name, payload.family_name].filter(Boolean).join(' ') ||
            payload.email,
          group: readGroup(payload),
        });
      },
      onFailure: reject,
      newPasswordRequired: () => resolve({ requiresNewPassword: true, cognitoUser: user }),
    });
  });
}

export function signOut() {
  const current = userPool?.getCurrentUser();
  if (current) current.signOut();
}

export function signUp(firstName, lastName, email, password) {
  return new Promise((resolve, reject) => {
    if (!userPool) {
      reject(new Error('Cognito is not configured.'));
      return;
    }
    const attrs = [
      new CognitoUserAttribute({ Name: 'email', Value: email }),
      new CognitoUserAttribute({ Name: 'given_name', Value: firstName }),
      new CognitoUserAttribute({ Name: 'family_name', Value: lastName }),
    ];
    userPool.signUp(email, password, attrs, null, (err, result) => {
      if (err) { reject(err); return; }
      resolve(result.user);
    });
  });
}

export function confirmSignUp(email, code) {
  return new Promise((resolve, reject) => {
    if (!userPool) { reject(new Error('Cognito is not configured.')); return; }
    const user = new CognitoUser({ Username: email, Pool: userPool });
    user.confirmRegistration(code, true, (err) => {
      if (err) { reject(err); return; }
      resolve();
    });
  });
}

export function resendConfirmationCode(email) {
  return new Promise((resolve, reject) => {
    if (!userPool) { reject(new Error('Cognito is not configured.')); return; }
    const user = new CognitoUser({ Username: email, Pool: userPool });
    user.resendConfirmationCode((err) => {
      if (err) { reject(err); return; }
      resolve();
    });
  });
}

export function completeNewPassword(cognitoUser, newPassword) {
  return new Promise((resolve, reject) => {
    cognitoUser.completeNewPasswordChallenge(newPassword, {}, {
      onSuccess: (session) => {
        const payload = session.getIdToken().decodePayload();
        resolve({
          email: payload.email,
          name:
            [payload.given_name, payload.family_name].filter(Boolean).join(' ') ||
            payload.email,
          group: readGroup(payload),
        });
      },
      onFailure: reject,
    });
  });
}

export function forgotPassword(email) {
  return new Promise((resolve, reject) => {
    if (!userPool) { reject(new Error('Cognito is not configured.')); return; }
    const user = new CognitoUser({ Username: email, Pool: userPool });
    user.forgotPassword({
      onSuccess: resolve,
      onFailure: reject,
    });
  });
}

export function confirmNewPassword(email, code, newPassword) {
  return new Promise((resolve, reject) => {
    if (!userPool) { reject(new Error('Cognito is not configured.')); return; }
    const user = new CognitoUser({ Username: email, Pool: userPool });
    user.confirmPassword(code, newPassword, {
      onSuccess: resolve,
      onFailure: reject,
    });
  });
}

export function changePassword(currentPassword, newPassword) {
  return new Promise((resolve, reject) => {
    const current = userPool?.getCurrentUser();
    if (!current) { reject(new Error('Not signed in.')); return; }
    current.getSession((err) => {
      if (err) { reject(err); return; }
      current.changePassword(currentPassword, newPassword, (err2) => {
        if (err2) { reject(err2); return; }
        resolve();
      });
    });
  });
}

export function getCurrentSession() {
  return new Promise((resolve) => {
    const current = userPool?.getCurrentUser();
    if (!current) {
      resolve(null);
      return;
    }
    current.getSession((err, session) => {
      if (err || !session?.isValid()) {
        resolve(null);
        return;
      }
      const payload = session.getIdToken().decodePayload();
      resolve({
        email: payload.email,
        name:
          [payload.given_name, payload.family_name].filter(Boolean).join(' ') ||
          payload.email,
        group: readGroup(payload),
        idToken: session.getIdToken().getJwtToken(),
      });
    });
  });
}
